import { AuthLayout } from '@/components/auth'
import { Icon } from '@tempots/beatui'
import { OneOfValue, attr, html, on, prop } from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { auth } from '@/services/gateway'

type ResendStatus = 'idle' | 'success' | 'error'

export function VerifyEmailSentPage() {
  // Extract URL params into signals for reactivity
  const params = new URLSearchParams(window.location.search)
  const email = prop(params.get('email') || '')
  const type = prop(params.get('type') || 'verification') // "verification" or "reset"

  const resending = prop(false)
  const resendStatus = prop<ResendStatus>('idle')
  const resendMessage = prop('')

  // Derive computed value from signal
  const isReset = type.map((t) => t === 'reset')

  const handleResend = async () => {
    const emailValue = email.value
    if (!emailValue || resending.value) return

    resending.set(true)
    resendStatus.set('idle')
    resendMessage.set('')

    try {
      if (type.value === 'reset') {
        await auth.forgotPassword({ email: emailValue })
      } else {
        await auth.resendVerification({ email: emailValue })
      }
      resendStatus.set('success')
      resendMessage.set('Email sent successfully!')
    } catch (error) {
      resendStatus.set('error')
      resendMessage.set(
        error instanceof Error ? error.message : 'Failed to resend email'
      )
    } finally {
      resending.set(false)
    }
  }

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full text-center space-y-8'),
      // Content
      html.div(
        attr.class('space-y-4'),
        html.h2(
          attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
          'Check your email'
        ),
        html.p(
          attr.class('text-sm sm:text-base text-gray-600'),
          isReset.map((reset) =>
            reset
              ? "We've sent a password reset link to "
              : "We've sent a verification link to "
          ),
          html.br(attr.class('hidden sm:inline')),
          html.span(
            attr.class('font-medium text-gray-900'),
            email.map((e) => e || 'your email')
          )
        )
      ),
      // Instructions
      html.div(
        attr.class('bg-gray-100 rounded-lg p-4 sm:p-6 text-left'),
        html.h3(
          attr.class('text-sm font-semibold text-gray-900 mb-3'),
          'Next steps:'
        ),
        html.ol(
          attr.class('space-y-2 text-sm text-gray-600'),
          html.li(
            attr.class('flex gap-3'),
            html.span(
              attr.class(
                'flex-shrink-0 w-5 h-5 flex items-center justify-center bg-white rounded-full text-xs font-medium text-gray-900'
              ),
              '1'
            ),
            html.span('Check your inbox for an email from Verts')
          ),
          html.li(
            attr.class('flex gap-3'),
            html.span(
              attr.class(
                'flex-shrink-0 w-5 h-5 flex items-center justify-center bg-white rounded-full text-xs font-medium text-gray-900'
              ),
              '2'
            ),
            html.span(
              isReset.map((reset) =>
                reset
                  ? 'Click the reset link in the email'
                  : 'Click the verification link in the email'
              )
            )
          ),
          html.li(
            attr.class('flex gap-3'),
            html.span(
              attr.class(
                'flex-shrink-0 w-5 h-5 flex items-center justify-center bg-white rounded-full text-xs font-medium text-gray-900'
              ),
              '3'
            ),
            html.span(
              isReset.map((reset) =>
                reset ? 'Create a new password' : 'Start using Verts!'
              )
            )
          )
        )
      ),
      // Resend status message
      OneOfValue(resendStatus, {
        idle: () => null,
        success: () =>
          html.div(
            attr.class(
              'bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm'
            ),
            resendMessage
          ),
        error: () =>
          html.div(
            attr.class(
              'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
            ),
            resendMessage
          ),
      }),
      // Actions
      html.div(
        attr.class('space-y-4'),
        html.p(
          attr.class('text-sm text-gray-500'),
          "Didn't receive the email? ",
          html.button(
            attr.type('button'),
            on.click(handleResend),
            attr.class(
              resending.map((r: boolean) =>
                r
                  ? 'font-medium text-gray-400 cursor-not-allowed'
                  : 'font-medium text-brand-600 hover:text-brand-700'
              )
            ),
            resending.map((r: boolean) => (r ? 'Sending...' : 'Resend'))
          )
        ),
        html.div(
          Anchor(
            { href: '/signin' },
            attr.class(
              'inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700'
            ),
            Icon({ icon: 'mdi:arrow-left' }, attr.class('w-4 h-4')),
            'Back to sign in'
          )
        )
      )
    )
  )
}
